import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { Upload, X, FileText, Shield, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

export default function QuoteFormPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    ad_soyad: '', telefon: '', email: '', sirket_adi: '',
    sehir: '', hizmet: '', aciklama: '', adet: '',
    malzeme: '', kalinlik: '', teslim_tarihi: '', kvkk_onay: false
  });

  useEffect(() => {
    publicAPI.getCities().then(r => setCities(r.data));
    publicAPI.getServices().then(r => setServices(r.data));
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files);
    const allowed = ['.dxf','.dwg','.step','.stp','.pdf','.jpg','.jpeg','.png'];
    const valid = newFiles.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return allowed.includes(ext) && f.size <= 100 * 1024 * 1024;
    });
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!form.ad_soyad.trim()) errs.ad_soyad = 'Ad Soyad zorunlu';
    if (!form.telefon.trim()) errs.telefon = 'Telefon zorunlu';
    if (!form.sehir) errs.sehir = 'Sehir secimi zorunlu';
    if (!form.hizmet) errs.hizmet = 'Hizmet secimi zorunlu';
    if (!form.aciklama.trim()) errs.aciklama = 'Aciklama zorunlu';
    if (!form.kvkk_onay) errs.kvkk_onay = 'KVKK onayi zorunlu';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === 'kvkk_onay') formData.append(key, val ? 'true' : 'false');
        else if (val) formData.append(key, val);
      });
      files.forEach(f => formData.append('dosyalar', f));
      await publicAPI.submitQuote(formData);
      navigate('/tesekkurler');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Form gonderilemedi. Lutfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <>
      <Helmet>
        <title>Teklif Al | Endustriyel Uretim Hizmetleri</title>
        <meta name="description" content="Endustriyel uretim projeniz icin teklif formu. Teknik dosyalarinizi yukleyin, detaylari belirtin." />
      </Helmet>

      <section className="bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-semibold mb-2" data-testid="quote-title">Teklif Al</h1>
          <p className="text-muted-foreground">Asagidaki formu doldurun, en kisa surede donus yapalim.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
            {/* Contact Info */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Iletisim Bilgileri</CardTitle></CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ad_soyad">Ad Soyad *</Label>
                  <Input id="ad_soyad" value={form.ad_soyad} onChange={e => handleChange('ad_soyad', e.target.value)} data-testid="input-ad-soyad" />
                  {errors.ad_soyad && <p className="text-xs text-red-600 mt-1" role="alert">{errors.ad_soyad}</p>}
                </div>
                <div>
                  <Label htmlFor="telefon">Telefon *</Label>
                  <Input id="telefon" type="tel" value={form.telefon} onChange={e => handleChange('telefon', e.target.value)} data-testid="input-telefon" />
                  {errors.telefon && <p className="text-xs text-red-600 mt-1" role="alert">{errors.telefon}</p>}
                </div>
                <div>
                  <Label htmlFor="email">E-posta</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} data-testid="input-email" />
                </div>
                <div>
                  <Label htmlFor="sirket_adi">Sirket Adi</Label>
                  <Input id="sirket_adi" value={form.sirket_adi} onChange={e => handleChange('sirket_adi', e.target.value)} data-testid="input-sirket" />
                </div>
              </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Hizmet Detaylari</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sehir">Sehir *</Label>
                    <select id="sehir" value={form.sehir} onChange={e => handleChange('sehir', e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-sehir">
                      <option value="">Sehir secin</option>
                      {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    {errors.sehir && <p className="text-xs text-red-600 mt-1" role="alert">{errors.sehir}</p>}
                  </div>
                  <div>
                    <Label htmlFor="hizmet">Hizmet *</Label>
                    <select id="hizmet" value={form.hizmet} onChange={e => handleChange('hizmet', e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-hizmet">
                      <option value="">Hizmet secin</option>
                      {services.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                    {errors.hizmet && <p className="text-xs text-red-600 mt-1" role="alert">{errors.hizmet}</p>}
                  </div>
                </div>
                <div>
                  <Label htmlFor="aciklama">Aciklama *</Label>
                  <textarea id="aciklama" value={form.aciklama} onChange={e => handleChange('aciklama', e.target.value)} rows={4} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" placeholder="Parca detaylari, toleranslar, ozel gereksinimler..." data-testid="input-aciklama" />
                  {errors.aciklama && <p className="text-xs text-red-600 mt-1" role="alert">{errors.aciklama}</p>}
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="adet">Adet</Label>
                    <Input id="adet" value={form.adet} onChange={e => handleChange('adet', e.target.value)} data-testid="input-adet" />
                  </div>
                  <div>
                    <Label htmlFor="malzeme">Malzeme</Label>
                    <Input id="malzeme" value={form.malzeme} onChange={e => handleChange('malzeme', e.target.value)} placeholder="Orn: Paslanmaz Celik" data-testid="input-malzeme" />
                  </div>
                  <div>
                    <Label htmlFor="kalinlik">Kalinlik / Olcu</Label>
                    <Input id="kalinlik" value={form.kalinlik} onChange={e => handleChange('kalinlik', e.target.value)} placeholder="Orn: 25mm" data-testid="input-kalinlik" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="teslim_tarihi">Istenen Teslim Tarihi</Label>
                  <Input id="teslim_tarihi" type="date" value={form.teslim_tarihi} onChange={e => handleChange('teslim_tarihi', e.target.value)} data-testid="input-teslim" />
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Dosya Yukleme</CardTitle></CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="quote-form-file-upload"
                >
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium text-sm">Dosyalarinizi yuklemek icin tiklayin</p>
                  <p className="text-xs text-muted-foreground mt-1">DXF, DWG, STEP, STP, PDF, JPG, PNG - Maks. 100 MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".dxf,.dwg,.step,.stp,.pdf,.jpg,.jpeg,.png"
                    onChange={handleFileAdd}
                    className="hidden"
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`file-${i}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{f.name}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(f.size)}</p>
                          </div>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-4 w-4" />
                  Dosyalariniz guvenli bir sekilde saklanir ve sadece yetkili kisiler tarafindan erisilebilir.
                </div>
              </CardContent>
            </Card>

            {/* KVKK */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="kvkk"
                checked={form.kvkk_onay}
                onCheckedChange={(val) => handleChange('kvkk_onay', val)}
                data-testid="checkbox-kvkk"
              />
              <div>
                <Label htmlFor="kvkk" className="text-sm leading-relaxed">
                  <a href="/kvkk" target="_blank" className="text-[hsl(221,83%,22%)] underline">KVKK Aydinlatma Metni</a>'ni okudum ve onayliyorum. *
                </Label>
                {errors.kvkk_onay && <p className="text-xs text-red-600 mt-1" role="alert">{errors.kvkk_onay}</p>}
              </div>
            </div>

            <Button type="submit" size="lg" className="w-full font-semibold" disabled={submitting} data-testid="submit-quote">
              {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Gonderiliyor...</> : 'Teklif Talebini Gonder'}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
